import React from 'react';
import PropTypes from 'prop-types';
import {
  LuBookOpen,
  LuCheck,
  LuChevronRight,
  LuCircleCheck,
  LuClipboardList,
  LuExternalLink,
  LuFileText,
  LuFolderOpen,
  LuGraduationCap,
  LuInfo,
  LuLock,
  LuPlay,
  LuScale,
  LuSearch,
  LuSparkles,
  LuBriefcaseBusiness,
  LuVideo,
} from 'react-icons/lu';
import Badge from '../../../../../components/ui/Badge';
import GlassIcon from '../../../../../components/ui/GlassIcon';
import { useTranslation } from '../../../../../i18n/LanguageContext';
import { Abs, Btn, CARD, Dialog, DialogIcon, Field, Note, SectionHead, Segmented, TILE } from '../kit';

const CATEGORIES = [
  { key: 'sop', icon: LuFileText, gradient: 'tw-from-teal-400 tw-to-emerald-600 tw-shadow-teal-500/30', stat: 'sopStat', newCount: 1 },
  { key: 'education', icon: LuGraduationCap, gradient: 'tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30', stat: 'educationStat', newCount: 1 },
  { key: 'forms', icon: LuClipboardList, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30', count: 2, newCount: 1 },
  { key: 'policies', icon: LuScale, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30', count: 3 },
  { key: 'jobDescriptions', icon: LuBriefcaseBusiness, gradient: 'tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30', count: 1 },
];

export const FILES = (top) => ({
  cards: { x: 256, y: top + 64, w: 1000, h: 124 },
  card: (index) => ({ x: 256 + index * 202, y: top + 64, w: 192, h: 124 }),
  filter: { x: 968, y: top + 200, w: 288, h: 34 },
  filterWidths: [64, 104, 104],
  note: { x: 256, y: top + 244, w: 1000, h: 44 },
  search: { x: 256, y: top + 300, w: 1000, h: 40 },
  row: (index) => ({ x: 256, y: top + 352 + index * 62, w: 1000, h: 56 }),
  list: { x: 256, y: top + 196, w: 1000, h: 344 },
});

const ROWS = {
  sop: [
    { key: 'sop1', code: 'SOP-014', meta: 'v3 · pdf', kind: 'pdf', badges: ['new', 'unread'] },
    { key: 'sop2', code: 'SOP-022', meta: 'v2 · pdf', kind: 'pdf', badges: ['unread'] },
    { key: 'sop3', code: 'SOP-009', meta: 'v5 · video', kind: 'video', badges: ['read'] },
  ],
  education: [
    { key: 'training1', meta: '45', deadline: 2, kind: 'video', badges: ['new'] },
    { key: 'training2', meta: '30', deadline: 6, kind: 'video', badges: [] },
    { key: 'training3', meta: '25', kind: 'video', badges: ['completed'] },
    { key: 'training4', meta: '20', kind: 'video', badges: ['expired'], expired: true },
  ],
  forms: [
    { key: 'form1', meta: 'v2', kind: 'form', badges: ['new'], external: true },
    { key: 'form2', meta: 'v1', kind: 'form', badges: [], external: true },
  ],
};
const KIND_TILE = {
  pdf: 'tw-bg-rose-500/10 tw-text-rose-600',
  video: 'tw-bg-violet-500/10 tw-text-violet-600',
  form: 'tw-bg-sky-500/10 tw-text-sky-600',
};
const BADGE_TONE = { new: 'warning', unread: 'neutral', read: 'success', completed: 'success', expired: 'danger' };

/**
 * Dosyalar: five category cards (SOP, Eğitimler, Formlar, Politikalar, Görev
 * Tanımları) and the open category's list below.
 * state: { category: 'sop'|'education'|'forms', filter, search, focusRow, pressedCard, sopRead }
 */
export const FilesPane = ({ top, state }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.files.${key}`, vars);
  const F = FILES(top);
  const category = state.category || 'sop';
  const rows = ROWS[category];
  const readDone = state.sopRead;
  const deadlineDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toLocaleDateString('tr-TR');
  };
  return (
    <>
      <SectionHead top={top} icon={LuFolderOpen} gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" title={label('title')} description={label('description')} />
      {CATEGORIES.map((item, index) => {
        const Icon = item.icon;
        const open = item.key === category;
        let stat = label('cards.count', { count: item.count || 0 });
        if (item.key === 'sop') stat = label('cards.sopStat', { read: readDone ? 7 : 6, total: 8 });
        if (item.key === 'education') stat = label('cards.educationStat', { done: 1, total: 4 });
        return (
          <Abs key={item.key} rect={F.card(index)} className={`tw-flex tw-flex-col tw-p-3.5 tw-transition-all tw-duration-300 ${CARD} ${open ? 'tw-ring-2 tw-ring-teal-400/60 tw-bg-teal-50/70 dark:tw-bg-teal-500/[0.06]' : ''} ${state.pressedCard === item.key ? 'tw-scale-[0.97]' : ''}`}>
            <div className="tw-flex tw-items-start tw-justify-between">
              <GlassIcon gradient={item.gradient} size="sm"><Icon aria-hidden="true" className="tw-h-4 tw-w-4" /></GlassIcon>
              {item.newCount && !(item.key === 'forms' && state.formsSeen) ? <span className="tw-rounded-full tw-bg-amber-400/20 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold tw-text-amber-700">{label('cards.new', { count: item.newCount })}</span> : null}
            </div>
            <p className="tw-mt-2 tw-truncate tw-text-[14px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label(`categories.${item.key}.title`)}</p>
            <p className="tw-truncate tw-text-[11px] tw-text-slate-500">{label(`categories.${item.key}.description`)}</p>
            <p className="tw-mt-auto tw-text-[11px] tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{stat}</p>
          </Abs>
        );
      })}
      <span style={{ left: 256, top: top + 206 }} className="tw-absolute tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label(`categories.${category}.title`)}</span>
      {category === 'sop' && (
        <Segmented
          rect={F.filter}
          value={state.filter || 'all'}
          small
          options={[
            { value: 'all', label: label('sopFilter.all'), w: F.filterWidths[0] },
            { value: 'unread', label: label('sopFilter.unread', { count: readDone ? 1 : 2 }), w: F.filterWidths[1] },
            { value: 'read', label: label('sopFilter.read', { count: readDone ? 2 : 1 }), w: F.filterWidths[2] },
          ]}
        />
      )}
      {category !== 'policies' && <Note rect={F.note} icon={LuInfo}>{label(`help.${category}`)}</Note>}
      <Field rect={F.search} value={state.search} placeholder={label(`search.${category}`)} prefix={<LuSearch aria-hidden="true" className="tw-h-4 tw-w-4" />} focused={state.searchFocus} />
      {rows.filter((row) => !state.filter || state.filter === 'all' || (state.filter === 'unread' ? row.badges.includes('unread') : row.badges.includes('read'))).map((row, index) => {
        const Kind = row.kind === 'video' ? LuVideo : row.kind === 'form' ? LuClipboardList : LuFileText;
        let badges = row.badges;
        if (row.key === 'sop1' && readDone) badges = ['read'];
        const isNew = badges.includes('new');
        return (
          <Abs key={row.key} rect={F.row(index)} className={`tw-flex tw-items-center tw-gap-3 tw-px-4 ${TILE} ${isNew ? 'tw-bg-amber-50/80 dark:tw-bg-amber-400/[0.06]' : ''} ${row.expired ? 'tw-opacity-55' : ''} ${state.focusRow === index ? 'tw-ring-2 tw-ring-teal-400/60' : ''}`}>
            <span className={`tw-grid tw-h-9 tw-w-9 tw-place-items-center tw-rounded-xl ${KIND_TILE[row.kind]}`}><Kind aria-hidden="true" className="tw-h-4 tw-w-4" /></span>
            <span className="tw-min-w-0 tw-flex-1">
              <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{row.code ? `${row.code} · ` : ''}{t(`userGuide.stage.${row.key}`)}</span>
              <span className="tw-block tw-text-[11px] tw-text-slate-500">
                {category === 'education' ? `${row.meta} dk` : row.meta.replace('pdf', label('kinds.pdf')).replace('video', label('kinds.video'))}
                {row.deadline ? <span className={`tw-ml-2 tw-font-semibold ${row.deadline <= 3 ? 'tw-text-rose-600' : 'tw-text-amber-600'}`}>{label('deadline', { date: deadlineDate(row.deadline) })}</span> : null}
              </span>
            </span>
            {badges.map((badge) => (
              <Badge key={badge} tone={BADGE_TONE[badge]}>
                {badge === 'new' && <LuSparkles aria-hidden="true" className="tw-h-3 tw-w-3" />}
                {(badge === 'read' || badge === 'completed') && <LuCheck aria-hidden="true" className="tw-h-3 tw-w-3" />}
                {label(`badges.${badge}`)}
              </Badge>
            ))}
            {row.external ? <LuExternalLink aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" /> : <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />}
          </Abs>
        );
      })}
    </>
  );
};

FilesPane.propTypes = { top: PropTypes.number.isRequired, state: PropTypes.object.isRequired };

// ── SOP viewer and the end-of-reading quiz ──────────────────────────────
export const SOP_DLG = {
  dialog: { x: 200, y: 36, w: 880, h: 648 },
  viewer: { x: 224, y: 146, w: 832, h: 330 },
  confirm: { x: 224, y: 488, w: 832, h: 52 },
  checkbox: { x: 240, y: 502, w: 24, h: 24 },
  hint: { x: 224, y: 548, w: 832, h: 44 },
  openInTab: { x: 900, y: 632, w: 156, h: 36 },
};

export const SopViewerDialog = ({ appear, unlocked, checked }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.files.viewer.${key}`, vars);
  return (
    <Dialog rect={SOP_DLG.dialog} appear={appear} footer title={`SOP-014 · ${t('userGuide.stage.sop1')}`} subtitle={label('title')} icon={<DialogIcon icon={LuFileText} gradient="tw-from-teal-400 tw-to-emerald-600 tw-shadow-teal-500/30" />}>
      <span style={{ left: 226, top: 118 }} className="tw-absolute tw-text-xs tw-text-slate-500">{label('version', { version: 3 })} · {t('profilePage.files.kinds.pdf')}</span>
      <Abs rect={SOP_DLG.viewer} className="tw-overflow-hidden tw-rounded-2xl tw-bg-slate-200/70 tw-p-5 dark:tw-bg-black/30">
        <div className="tw-mx-auto tw-h-full tw-w-[520px] tw-rounded-md tw-bg-white tw-p-6 tw-shadow-lg">
          <p className="tw-text-sm tw-font-bold tw-text-slate-800">SOP-014 · {t('userGuide.stage.sop1')}</p>
          {Array.from({ length: 11 }, (_, index) => (
            <span key={index} style={{ width: `${70 + ((index * 37) % 30)}%` }} className="tw-mt-3 tw-block tw-h-2 tw-rounded tw-bg-slate-200" />
          ))}
        </div>
      </Abs>
      <Abs rect={SOP_DLG.confirm} className={`tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-px-4 tw-transition-colors tw-duration-300 ${checked ? 'tw-border-emerald-400/60 tw-bg-emerald-500/10' : unlocked ? 'tw-border-teal-400/50 tw-bg-white dark:tw-bg-white/[0.04]' : 'tw-border-slate-200 tw-bg-slate-50 dark:tw-border-white/10 dark:tw-bg-white/[0.02]'}`}>
        <span className={`tw-grid tw-h-6 tw-w-6 tw-place-items-center tw-rounded-md tw-border-2 ${checked ? 'tw-border-emerald-500 tw-bg-emerald-500 tw-text-white' : unlocked ? 'tw-border-teal-500' : 'tw-border-slate-300 tw-text-slate-400'}`}>
          {checked ? <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" /> : !unlocked && <LuLock aria-hidden="true" className="tw-h-3 tw-w-3" />}
        </span>
        <span className={`tw-text-sm tw-font-semibold ${unlocked ? 'tw-text-slate-800 dark:tw-text-slate-100' : 'tw-text-slate-400'}`}>{label('confirm')}</span>
      </Abs>
      <Note rect={SOP_DLG.hint} tone={unlocked ? 'info' : 'warning'} icon={LuInfo}>{unlocked ? label('quizHint') : label('lockedHint')}</Note>
      <Btn rect={{ ...SOP_DLG.openInTab, x: SOP_DLG.openInTab.x - 104, w: 96 }} variant="ghost">{t('common.close')}</Btn>
      <Btn rect={SOP_DLG.openInTab} icon={LuExternalLink}>{label('openInTab')}</Btn>
    </Dialog>
  );
};

SopViewerDialog.propTypes = { appear: PropTypes.number, unlocked: PropTypes.bool, checked: PropTypes.bool };

export const QUIZ_DLG = {
  dialog: { x: 320, y: 70, w: 640, h: 580 },
  choice: (index) => ({ x: 344, y: 268 + index * 56, w: 592, h: 46 }),
  previous: { x: 344, y: 600, w: 100, h: 36 },
  next: { x: 820, y: 600, w: 116, h: 36 },
};

export const QuizDialog = ({ appear, question = 1, answer = -1, result }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.quiz.${key}`, vars);
  const choices = ['a', 'b', 'c', 'd'];
  return (
    <Dialog rect={QUIZ_DLG.dialog} appear={appear} z={40} footer title={label('title')} subtitle={`SOP-014 · ${t('userGuide.stage.sop1')}`} icon={<DialogIcon icon={LuBookOpen} gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-indigo-500/30" />}>
      {result ? (
        <>
          <div style={{ left: 320, top: 190, width: 640 }} className="tw-absolute tw-flex tw-flex-col tw-items-center">
            <span className="tw-grid tw-h-24 tw-w-24 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-emerald-400 tw-to-teal-600 tw-text-3xl tw-font-bold tw-text-white tw-shadow-xl tw-shadow-emerald-500/30">{label('percent', { value: 80 })}</span>
            <p className="tw-mt-4 tw-text-lg tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label('correctOf', { correct: 4, total: 5 })}</p>
            <span className="tw-mt-2"><Badge tone="success"><LuCircleCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />{label('passed')}</Badge></span>
          </div>
          <Btn rect={QUIZ_DLG.next} variant="primary" icon={LuCheck}>{label('complete')}</Btn>
        </>
      ) : (
        <>
          <span style={{ left: 344, top: 158 }} className="tw-absolute tw-text-xs tw-font-semibold tw-text-slate-500">{label('questionOf', { current: question, total: 5 })}</span>
          <span style={{ left: 836, top: 158 }} className="tw-absolute tw-text-xs tw-font-semibold tw-text-teal-700">{label('passScore', { score: 70 })}</span>
          <span style={{ left: 344, top: 180, width: 592 }} className="tw-absolute tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-slate-200 dark:tw-bg-white/10">
            <span style={{ width: `${(question / 5) * 100}%` }} className="tw-block tw-h-full tw-rounded-full tw-bg-teal-500" />
          </span>
          <p style={{ left: 344, top: 204, width: 592 }} className="tw-absolute tw-text-[15px] tw-font-semibold tw-leading-snug tw-text-slate-900 dark:tw-text-white">{t('userGuide.stage.quizQuestion')}</p>
          {choices.map((key, index) => (
            <Abs key={key} rect={QUIZ_DLG.choice(index)} className={`tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-px-3 tw-text-sm tw-transition-colors tw-duration-300 ${answer === index ? 'tw-border-teal-500 tw-bg-teal-500/10 tw-font-semibold tw-text-teal-900 dark:tw-text-teal-100' : 'tw-border-slate-200 tw-text-slate-700 dark:tw-border-white/10 dark:tw-text-slate-200'}`}>
              <span className={`tw-grid tw-h-7 tw-w-7 tw-place-items-center tw-rounded-lg tw-text-xs tw-font-bold ${answer === index ? 'tw-bg-teal-500 tw-text-white' : 'tw-bg-slate-100 tw-text-slate-500 dark:tw-bg-white/10'}`}>{key.toUpperCase()}</span>
              {t(`userGuide.stage.quizChoice${index + 1}`)}
            </Abs>
          ))}
          <Btn rect={QUIZ_DLG.previous}>{label('previous')}</Btn>
          <Btn rect={QUIZ_DLG.next} variant="primary">{question >= 5 ? label('finish') : label('next')}</Btn>
        </>
      )}
    </Dialog>
  );
};

QuizDialog.propTypes = { appear: PropTypes.number, question: PropTypes.number, answer: PropTypes.number, result: PropTypes.bool };

// ── Training player ─────────────────────────────────────────────────────
export const TRAIN_DLG = {
  dialog: { x: 56, y: 28, w: 1168, h: 664 },
  video: { x: 80, y: 116, w: 780, h: 440 },
  timeline: { x: 100, y: 516, w: 740, h: 20 },
  chapters: { x: 880, y: 116, w: 320, h: 320 },
  final: { x: 880, y: 448, w: 320, h: 108 },
  startExam: { x: 896, y: 504, w: 288, h: 38 },
  pop: { x: 380, y: 140, w: 520, h: 440 },
  popChoice: (index) => ({ x: 404, y: 316 + index * 54, w: 472, h: 44 }),
  popSubmit: { x: 720, y: 528, w: 156, h: 38 },
};

export const TrainingDialog = ({ appear, progress = 0.62, pop, popAnswer = -1, examReady }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.education.${key}`, vars);
  const chapters = ['introduction', 'coreConcepts', 'practice', 'summary'];
  const reached = Math.floor(progress * 4);
  return (
    <Dialog rect={TRAIN_DLG.dialog} appear={appear} title={t('userGuide.stage.training1')} subtitle={`Dr. Elif Arslan · 45 dk`} titleRight={140} icon={<DialogIcon icon={LuGraduationCap} gradient="tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30" />}>
      <span style={{ left: 1040, top: 52 }} className="tw-absolute"><Badge tone={progress >= 0.95 ? 'success' : 'info'}>{label('watchedBadge', { value: Math.round(progress * 100) })}</Badge></span>
      <Abs rect={TRAIN_DLG.video} className="tw-overflow-hidden tw-rounded-2xl tw-bg-gradient-to-br tw-from-slate-800 tw-to-slate-950">
        <span className="tw-absolute tw-inset-0 tw-grid tw-place-items-center">
          <span className="tw-grid tw-h-16 tw-w-16 tw-place-items-center tw-rounded-full tw-bg-white/15 tw-text-white"><LuPlay aria-hidden="true" className="tw-ml-1 tw-h-8 tw-w-8" /></span>
        </span>
        <span className="tw-absolute tw-left-6 tw-top-5 tw-text-sm tw-font-semibold tw-text-white/80">{label(`sections.${chapters[Math.min(3, reached)]}`)}</span>
      </Abs>
      <Abs rect={TRAIN_DLG.timeline}>
        <span className="tw-absolute tw-inset-x-0 tw-top-2 tw-h-1.5 tw-rounded-full tw-bg-white/20" />
        <span style={{ width: `${progress * 100}%` }} className="tw-absolute tw-left-0 tw-top-2 tw-h-1.5 tw-rounded-full tw-bg-teal-400" />
        {[0.3, 0.7].map((at) => (
          <span key={at} style={{ left: `calc(${at * 100}% - 6px)` }} className={`tw-absolute tw-top-[3px] tw-h-3 tw-w-3 tw-rotate-45 tw-rounded-sm ${progress > at ? 'tw-bg-emerald-400' : 'tw-bg-amber-400'}`} />
        ))}
      </Abs>
      <Abs rect={TRAIN_DLG.chapters} className={`tw-p-4 ${TILE}`}>
        <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400">{label('contents')}</p>
        <div className="tw-mt-3 tw-space-y-2">
          {chapters.map((key, index) => (
            <span key={key} className={`tw-flex tw-h-11 tw-items-center tw-gap-3 tw-rounded-xl tw-px-3 tw-text-sm ${index === reached ? 'tw-bg-teal-500/10 tw-font-semibold tw-text-teal-900 dark:tw-text-teal-100' : 'tw-text-slate-600 dark:tw-text-slate-300'}`}>
              {index < reached ? <LuCircleCheck aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-500" /> : index === reached ? <LuPlay aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-teal-600" /> : <LuLock aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />}
              {label(`sections.${key}`)}
            </span>
          ))}
        </div>
      </Abs>
      <Abs rect={TRAIN_DLG.final} className={`tw-p-4 ${TILE}`}>
        <p className="tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{label('finalQuiz')}</p>
        {!examReady && <p className="tw-mt-1 tw-text-xs tw-text-slate-500">{label('finalLocked')}</p>}
      </Abs>
      {examReady && <Btn rect={TRAIN_DLG.startExam} variant="primary">{label('startExam')}</Btn>}
      {pop && (
        <Dialog rect={TRAIN_DLG.pop} z={40} title={label('popQuizTitle')} subtitle={label('popQuizSubtitle')} icon={<DialogIcon icon={LuSparkles} gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" />}>
          <p style={{ left: 404, top: 230, width: 472 }} className="tw-absolute tw-text-[15px] tw-font-semibold tw-leading-snug tw-text-slate-900 dark:tw-text-white">{t('userGuide.stage.popQuestion')}</p>
          {[1, 2, 3].map((index) => (
            <Abs key={index} rect={TRAIN_DLG.popChoice(index - 1)} className={`tw-flex tw-items-center tw-rounded-xl tw-border tw-px-3 tw-text-sm tw-transition-colors tw-duration-300 ${popAnswer === index - 1 ? 'tw-border-teal-500 tw-bg-teal-500/10 tw-font-semibold tw-text-teal-900 dark:tw-text-teal-100' : 'tw-border-slate-200 tw-text-slate-700 dark:tw-border-white/10 dark:tw-text-slate-200'}`}>
              {t(`userGuide.stage.popChoice${index}`)}
            </Abs>
          ))}
          <Btn rect={TRAIN_DLG.popSubmit} variant="primary">{label('submitAnswer')}</Btn>
        </Dialog>
      )}
    </Dialog>
  );
};

TrainingDialog.propTypes = { appear: PropTypes.number, progress: PropTypes.number, pop: PropTypes.bool, popAnswer: PropTypes.number, examReady: PropTypes.bool };
